import { useState } from 'react'
import type { CalcOutput } from '../lib/types'
import { RangeBar } from './RangeBar'
import { ShareIconButton } from './ShareIconButton'
import { getStrings, type Lang, type Strings } from '../i18n'

function confidenceTier(low: number, high: number, t: Strings) {
  const width = high - low
  if (width <= 300) return { label: t.result.tierTight, cls: 'chip-tight' }
  if (width <= 500) return { label: t.result.tierCalibrated, cls: 'chip-calibrated' }
  return { label: t.result.tierWide, cls: 'chip-wide' }
}

function bmrFormula(method: 'mifflin' | 'katch', t: Strings) {
  return method === 'mifflin' ? t.result.bmrMifflin : t.result.bmrKatch
}

export function ResultCard({
  result,
  onRecalculate,
  lang = 'en',
}: {
  result: CalcOutput
  onRecalculate: () => void
  lang?: Lang
}) {
  const t = getStrings(lang)
  const [showBreakdown, setShowBreakdown] = useState(true)

  const {
    tdee_low,
    tdee_high,
    tdee_recommended,
    recommendation_reason,
    components,
    bmr_kcal,
    bmr_method,
    handoff_token,
  } = result

  const tier = confidenceTier(tdee_low, tdee_high, t)

  return (
    <div className="result-card">
      <header className="result-header">
        <span className="kicker">{t.result.kicker}</span>
        <span className="result-header-right">
          <span className={`confidence-chip ${tier.cls}`}>{tier.label}</span>
          <ShareIconButton handoffToken={handoff_token} />
        </span>
      </header>

      <div className="range-display">
        <div className="range-line">
          <span>{tdee_low}</span>
          <span className="range-dash">–</span>
          <span>{tdee_high}</span>
          <span className="range-unit">{t.result.unit}</span>
        </div>
        <RangeBar low={tdee_low} high={tdee_high} marker={tdee_recommended} lang={lang} />
        <div className="recommended">
          <span className="label">{t.result.recommended}</span>
          <span className="value">{tdee_recommended}</span>
          <span className="unit">{t.result.unit}</span>
        </div>
      </div>

      <p className="reason">{recommendation_reason}</p>

      <section className="work-section">
        <div className="work-section-header">
          <span className="kicker">{t.result.showWork}</span>
          <button
            type="button"
            className="work-toggle"
            onClick={() => setShowBreakdown((v) => !v)}
            aria-expanded={showBreakdown}
          >
            {showBreakdown ? t.result.hide : t.result.show}
          </button>
        </div>
        {showBreakdown && (
          <table className="breakdown">
            <tbody>
              <tr className="row-section">
                <th>
                  {t.result.bmr}
                  <span className="source-hint">{bmrFormula(bmr_method, t)}</span>
                </th>
                <td colSpan={2}>{bmr_kcal}</td>
              </tr>

              <tr className="row-section">
                <th>
                  {t.result.neat}
                  <span className="source-hint">{t.result.neatHint}</span>
                </th>
                <td>{components.neat.low}</td>
                <td>{components.neat.high}</td>
              </tr>
              <tr className="row-sub">
                <th>
                  {t.result.occupation}
                  <span className="source-hint">{t.result.occupationHint}</span>
                </th>
                <td>{components.neat.breakdown.occupation.low}</td>
                <td>{components.neat.breakdown.occupation.high}</td>
              </tr>
              {components.neat.breakdown.steps && (
                <tr className="row-sub">
                  <th>
                    {t.result.steps}
                    <span className="source-hint">{t.result.stepsHint}</span>
                  </th>
                  <td>{components.neat.breakdown.steps.low}</td>
                  <td>{components.neat.breakdown.steps.high}</td>
                </tr>
              )}
              <tr className="row-sub">
                <th>
                  {t.result.commute}
                  <span className="source-hint">{t.result.commuteHint}</span>
                </th>
                <td>{components.neat.breakdown.commute.low}</td>
                <td>{components.neat.breakdown.commute.high}</td>
              </tr>
              <tr className="row-sub">
                <th>
                  {t.result.household}
                  <span className="source-hint">{t.result.householdHint}</span>
                </th>
                <td>{components.neat.breakdown.household.low}</td>
                <td>{components.neat.breakdown.household.high}</td>
              </tr>

              <tr className="row-section">
                <th>
                  {t.result.tef}
                  <span className="source-hint">{t.result.tefHint}</span>
                </th>
                <td>{components.tef.low}</td>
                <td>{components.tef.high}</td>
              </tr>

              <tr className="row-section">
                <th>
                  {t.result.eat}
                  <span className="source-hint">{t.result.eatHint}</span>
                </th>
                <td>{components.eat.low}</td>
                <td>{components.eat.high}</td>
              </tr>
              {components.eat.sessions.map((s, i) => (
                <tr className="row-sub" key={i}>
                  <th>
                    {s.activity}
                    <span className="source-hint">
                      {s.mets} MET · {t.result.metCompendium}
                    </span>
                  </th>
                  <td>{s.weekly_kcal_low}{t.result.perWk}</td>
                  <td>{s.weekly_kcal_high}{t.result.perWk}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="result-actions">
        <button
          type="button"
          className="recalc-btn"
          onClick={onRecalculate}
          title={t.result.recalcTitle}
        >
          {t.result.recalc}
        </button>
      </div>
    </div>
  )
}
